import React, { useEffect, useState } from "react";
import "./matrix.style.scss";
import {
  __MatrixProps,
  __MatrixRef,
  __MatrixItemType,
  __MatrixValue,
} from "./matrix.interface";
import { useRef } from "react";
import { Accordion, Card } from "react-bootstrap";
import ResizeObserver from "resize-observer-polyfill";
import { TextDirection } from "interface";
import HintText from "../hintText/hintText.index";
import QuestionImage from "../questionImage/questionImage.index";
import { extractTitle } from "scripts/string";
export interface SLPMatrixRef extends __MatrixRef {}
export interface MatrixItemType extends __MatrixItemType {}
export interface MatrixValue extends __MatrixValue {}

const SLPMatrix: React.ForwardRefRenderFunction<__MatrixRef, __MatrixProps> = (
  props: __MatrixProps,
  ref
) => {
  const [value, setValue] = useState<__MatrixValue[]>(
    props.defaultValue ? props.defaultValue : []
  );
  const immediateValue = useRef<__MatrixValue[]>(value);
  const [error, setError] = useState<string>();
  const tableContainerRef = useRef<HTMLDivElement>(null);
  const [isCollapse, setCollapse] = useState(false);
  const [activeIndex, setActiveIndex] = useState<number>();
  const breakPoint = useRef<number>(0);
  const labelRef = useRef<HTMLLabelElement>(null);

  const observerInit = () => {
    try {
      return new ResizeObserver(() => {
        checkSize();
      });
    } catch (e) {
      if (!isCollapse) setCollapse(true);
      return undefined;
    }
  };

  let observer = React.useRef<ResizeObserver | undefined>(observerInit());

  function checkSize() {
    if (
      !breakPoint.current &&
      tableContainerRef.current?.clientWidth &&
      tableContainerRef.current?.scrollWidth &&
      tableContainerRef.current?.clientWidth <
        tableContainerRef.current?.scrollWidth
    ) {
      setCollapse(true);
      breakPoint.current = tableContainerRef.current?.scrollWidth + 20;
    } else if (
      breakPoint.current &&
      tableContainerRef.current?.clientWidth &&
      tableContainerRef.current?.clientWidth > breakPoint.current
    ) {
      setCollapse(false);
      breakPoint.current = 0;
    }
  }

  useEffect(() => {
    if (labelRef.current && props.label)
      labelRef.current.innerHTML = extractTitle(props.label, false);
  }, [props.label]);

  useEffect(() => {
    if (tableContainerRef.current) {
      observer?.current?.observe(tableContainerRef.current);
    }

    return () => {
      if (tableContainerRef.current)
        observer?.current?.unobserve(tableContainerRef.current);
    };
  }, [tableContainerRef, observer]);

  React.useImperativeHandle(ref, () => ({
    getValue: () => immediateValue.current,
    getValidation: () => (props.required === true ? value.length > 0 : true),
    // setValue,
    setError: (text) => {
      setError(text);
    },
  }));

  function onSelectItem(row: __MatrixItemType, col: __MatrixItemType) {
    setError(undefined);
    let _value = [...value];
    const rowIndex = _value.findIndex((i) => i.row.payload === row.payload);
    let colIndex: number | undefined = undefined;
    if (rowIndex !== undefined && rowIndex > -1) {
      colIndex = _value[rowIndex].columns.findIndex(
        (i) => i.payload === col.payload
      );
      if (colIndex !== undefined && colIndex > -1) {
        _value[rowIndex].columns = [
          ..._value[rowIndex].columns.filter(
            (item) => item.payload === colIndex
          ),
        ];
      } else {
        _value[rowIndex].columns = [
          ...(props.multiSelect ? _value[rowIndex].columns : []),
          col,
        ];
      }
    } else {
      _value.push({ row: row, columns: [col] });
    }
    _value = _value.filter((item) => item.columns.length > 0);
    immediateValue.current = _value;
    setValue(immediateValue.current);
    if (props.onChange) props.onChange(_value);
  }

  return (
    <div
      className={`u-check-SLP-matrix  ${
        props.dir === TextDirection.rtl ? "rtl-text" : ""
      }`}
    >
      <div className="input-header">
        {props.required && <span className="required-star">*</span>}
        <span className="counter-span">
          {props.counter ? props.counter + "- " : ""}
        </span>
        <label ref={labelRef} style={{ color: props.labelColor }} />
      </div>
      <QuestionImage src={props.image?.file} />
      <HintText value={props.hint} />
      <div
        ref={tableContainerRef}
        className="matrix-container d-flex flex-column "
      >
        <table className={isCollapse ? "d-none" : ""}>
          <thead>
            <tr>
              <th></th>
              {props.items.length > 0 &&
                props.items[0].columns.map((col, index) => (
                  <th
                    dangerouslySetInnerHTML={{ __html: col.title }}
                    key={index}
                  ></th>
                ))}
            </tr>
          </thead>
          <tbody>
            {props.items
              .sort((a, b) => Number(a.row.order) - Number(b.row.order))
              .map((item, rIndex) => (
                <tr key={rIndex}>
                  <td dangerouslySetInnerHTML={{ __html: item.row.title }}></td>
                  {item.columns
                    .sort((a, b) => Number(a.order) - Number(b.order))
                    .map((colItem, indexCol) => (
                      <td key={indexCol}>
                        <label
                          className="ucheck-checkbox"
                          htmlFor={`table-check-${
                            props.id ? props.id : "0"
                          }-r-${rIndex}-c-${indexCol}`}
                        >
                          <input
                            onClick={() => onSelectItem(item.row, colItem)}
                            checked={value
                              .find((i) => i.row.payload === item.row.payload)
                              ?.columns.map((j) => j.payload)
                              .includes(colItem.payload)}
                            type="checkbox"
                            id={`table-check-${
                              props.id ? props.id : "0"
                            }-r-${rIndex}-c-${indexCol}`}
                            name={item.row.title}
                          />
                        </label>
                      </td>
                    ))}
                </tr>
              ))}
          </tbody>
        </table>
        <Accordion className={!isCollapse ? "d-none" : ""}>
          {props.items
            .sort((a, b) => Number(a.row.order) - Number(b.row.order))
            .map((item, rIndex) => (
              <Card
                key={rIndex}
                className={`${activeIndex === rIndex ? "active" : ""}`}
              >
                <Accordion.Toggle
                  as={Card.Header}
                  eventKey={rIndex.toString()}
                  onClick={() => {
                    setActiveIndex(activeIndex === rIndex ? undefined : rIndex);
                  }}
                >
                  <span>{item.row.title}</span>
                  <i className={`material-icons-outlined rotate-icon`}>
                    expand_more
                  </i>
                </Accordion.Toggle>
                <Accordion.Collapse eventKey={rIndex.toString()}>
                  <Card.Body>
                    <table>
                      {item.columns
                        .sort((a, b) => Number(a.order) - Number(b.order))
                        .map((colItem, indexCol) => (
                          <tr key={indexCol}>
                            <td>
                              <label className="ucheck-checkbox">
                                {colItem.title}
                              </label>
                            </td>
                            <td>
                              <label
                                className="ucheck-checkbox"
                                htmlFor={`collapse-check-${
                                  props.id ? props.id : "0"
                                }-r-${rIndex}-c-${indexCol}`}
                              >
                                <input
                                  onClick={() =>
                                    onSelectItem(item.row, colItem)
                                  }
                                  checked={value
                                    .find(
                                      (i) => i.row.payload === item.row.payload
                                    )
                                    ?.columns.map((j) => j.payload)
                                    .includes(colItem.payload)}
                                  type="checkbox"
                                  id={`collapse-check-${
                                    props.id ? props.id : "0"
                                  }-r-${rIndex}-c-${indexCol}`}
                                  name={item.row.title}
                                />
                              </label>
                            </td>
                          </tr>
                        ))}
                    </table>
                  </Card.Body>
                </Accordion.Collapse>
              </Card>
            ))}
        </Accordion>
      </div>
      {error && <p className="matrix-error">{error}</p>}
    </div>
  );
};

export default React.forwardRef(SLPMatrix);
